import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import OnlinePayment from "components/OnlinePayment/OnlinePayment";
import LoanPayment from "components/LoanPayment/LoanPayment";
import {
  DetailDisplay,
  Dialog,
  ErrorMessage,
  SingleSelect,
  Text,
  Toggle,
} from "widgets";
import PaymentDetails from "components/PaymentDetails/PaymentDetails";
import { useSelectedOrderDetails } from "store/backend/assets";
import DisplayOrder from "components/DisplayOrder/DisplayOrder";
import {
  INR_SYMBOL,
  SUCCESSFUL_REFUND_STATUSES,
  PAYMENT_STATUSES,
  SUCCESSFUL_PAYMENT_STATUSES,
  PAYMENT_METHODS,
  FULL_PAYMENT,
  PART_PAYMENT,
  FULL_PAYMENT_FLOW_TYPE_ID,
  LEARN_VERTICAL_ID,
  LOAN_PAYMENT_METHOD,
  PART_PAYMENT_FLOW_TYPE_ID,
  KH_VERTICAL_ID,
} from "constants/config";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddNote from "components/AddNote/AddNote";
import PartPayment from "components/PartPayment/PartPayment";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";

import styles from "./PaymentPreference.module.scss";

const SWITCH_TO_LOAN_PAYMENT = "Switch to Loan Payment method";
const SWITCH_TO_ONLINE_PAYMENT = "Switch to Online Payment method";

const handleTogglePaymentMethod = (event, setIsOnlinePayment) => {
  setIsOnlinePayment(event.target.checked);
};

const handleDialogClose = (setOpenDialogContent) => () => {
  setOpenDialogContent(null);
};

const handleOnClickAddNote = (setOpenDialogContent) => {
  setOpenDialogContent(true);
};
const showSelectPaymentMethodDropdown = (
  selectedOrderDetails,
  selectedOrderVerticalId
) => {
  if (selectedOrderVerticalId === LEARN_VERTICAL_ID) {
    return false;
  }
  if (selectedOrderDetails.payment_flow_type === null) {
    return true;
  }
};

const isLoanPaymentPresent = (paymentDetails) => {
  return paymentDetails.some(
    (payment) =>
      payment.method?.id === LOAN_PAYMENT_METHOD &&
      payment.status.id === PAYMENT_STATUSES.CREATED
  );
};
const PaymentPreference = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const salesPersonId = localStorage.getItem("sales_person_id");
  const [isOnlinePayment, setIsOnlinePayment] = useState(true);
  const [openDialogContent, setOpenDialogContent] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState();

  const {
    isPending: isSelectedOrderDetailsLoading,
    data: selectedOrderDetails,
    isError: isErrorOnSelectedOrderDetails,
    error: errorMessageOnSelectedOrderDetails,
  } = useSelectedOrderDetails(orderId);

  const isPartPaymentScheduleConfirmed =
    selectedOrderDetails?.part_payment_details?.length > 0 || false;

  const selectedOrderVerticalId =
    selectedOrderDetails && selectedOrderDetails.business_unit_id;

  const isAnyAmountPaid =
    selectedOrderDetails && selectedOrderDetails.amount_paid > 0;

  const selectedFlowType =
    selectedOrderDetails && selectedOrderDetails.payment_flow_type?.id;

  const headAmount =
    String(selectedOrderVerticalId) === String(KH_VERTICAL_ID)
      ? (() => {
          const paymentHeads =
            selectedOrderDetails?.packages?.[0]?.subscriptions?.[0]
              ?.payment_heads;

          if (!Array.isArray(paymentHeads) || paymentHeads.length === 0) {
            return undefined;
          }

          const firstHead = paymentHeads[0];
          return Number(firstHead.amount_paid) < Number(firstHead.amount)
            ? firstHead.amount
            : undefined;
        })()
      : undefined;

  useEffect(() => {
    if (isPartPaymentScheduleConfirmed) {
      setPaymentMethod(PART_PAYMENT);
    } else if (selectedOrderVerticalId === LEARN_VERTICAL_ID) {
      setPaymentMethod(FULL_PAYMENT);
    } else if (selectedFlowType === FULL_PAYMENT_FLOW_TYPE_ID) {
      setPaymentMethod(FULL_PAYMENT);
    } else if (selectedFlowType === PART_PAYMENT_FLOW_TYPE_ID) {
      setPaymentMethod(PART_PAYMENT);
    }
  }, [isPartPaymentScheduleConfirmed, selectedOrderDetails]);

  if (isSelectedOrderDetailsLoading) {
    return <FullPageLoader />;
  }

  if (isErrorOnSelectedOrderDetails) {
    return <div>{errorMessageOnSelectedOrderDetails.message}</div>;
  }

  if (!selectedOrderDetails) {
    return (
      <ErrorMessage>
        Order Details not found for Order Id {orderId}
      </ErrorMessage>
    );
  }

  const totalAmount = reduceToFixedDecimals(
    selectedOrderDetails.inr_conversion_amount
  );

  const totalSuccessfulPaymentAmount = reduceToFixedDecimals(
    selectedOrderDetails.payment_details
      .filter((payment) =>
        SUCCESSFUL_PAYMENT_STATUSES.includes(payment.status.id)
      )
      .reduce((sum, payment) => sum + payment.amount, 0)
  );

  const totalSuccessfulRefundedAmount = reduceToFixedDecimals(
    selectedOrderDetails.refund_details
      .filter((refund) => SUCCESSFUL_REFUND_STATUSES.includes(refund.status.id))
      .reduce((sum, refund) => sum + refund.refunded_amount, 0)
  );

  const pendingAmount = Math.max(
    0,
    reduceToFixedDecimals(
      totalAmount - totalSuccessfulPaymentAmount + totalSuccessfulRefundedAmount
    )
  );

  const paymentUnderProcessing = reduceToFixedDecimals(
    selectedOrderDetails.payment_details.length > 0
      ? selectedOrderDetails.payment_details
          .filter((payment) => payment.status.id === PAYMENT_STATUSES.CREATED)
          .reduce((sum, payment) => sum + payment.amount, 0)
      : 0
  );

  const amount_paid = reduceToFixedDecimals(
    totalSuccessfulPaymentAmount - totalSuccessfulRefundedAmount
  );

  const excess_amount_paid = Math.min(
    0,
    reduceToFixedDecimals(
      totalAmount - totalSuccessfulPaymentAmount + totalSuccessfulRefundedAmount
    )
  );

  const isLoanCreated = isLoanPaymentPresent(
    selectedOrderDetails.payment_details
  );

  return (
    <div className={styles.mainContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.paymentArea}>
          {showSelectPaymentMethodDropdown(
            selectedOrderDetails,
            selectedOrderVerticalId
          ) && (
            <div className={styles.field}>
              <Text>Select Payment Method</Text>
              <SingleSelect
                value={paymentMethod}
                menuItems={PAYMENT_METHODS}
                handleChange={(event) => setPaymentMethod(event.target.value)}
                tag="Select Payment Method"
              />
            </div>
          )}
          {paymentMethod === FULL_PAYMENT && !isPartPaymentScheduleConfirmed ? (
            isOnlinePayment ? (
              <OnlinePayment
                totalAmount={totalAmount}
                payableAmount={pendingAmount}
                orderId={orderId}
                paymentFlowTypeId={FULL_PAYMENT_FLOW_TYPE_ID}
                salesPersonId={salesPersonId}
                headAmount={headAmount}
              />
            ) : (
              <LoanPayment
                selected_order_id={orderId}
                pendingAmount={pendingAmount}
                totalAmount={totalAmount}
                selectedOrderVerticalId={selectedOrderVerticalId}
                salesPersonId={salesPersonId}
              />
            )
          ) : isPartPaymentScheduleConfirmed ||
            paymentMethod === PART_PAYMENT ? (
            <PartPayment
              selectedOrderId={orderId}
              isPartPaymentScheduleConfirmed={isPartPaymentScheduleConfirmed}
              selectedPartPaymentDetails={
                selectedOrderDetails.part_payment_details
              }
              isAnyAmountPaid={isAnyAmountPaid}
              salesPersonId={salesPersonId}
            />
          ) : null}
        </div>
        <div className={styles.orderDetails}>
          <div className={styles.amountDetails}>
            <DetailDisplay
              title="Total Order Amount"
              value={INR_SYMBOL + reduceToFixedDecimals(totalAmount)}
            />
            <DetailDisplay
              title="Total Amount Paid"
              value={INR_SYMBOL + reduceToFixedDecimals(amount_paid)}
            />
            <DetailDisplay
              title="Total Pending Amount"
              value={
                INR_SYMBOL + Math.abs(reduceToFixedDecimals(pendingAmount))
              }
            />
            <DetailDisplay
              title="Amount Under Processing"
              value={
                INR_SYMBOL +
                Math.abs(reduceToFixedDecimals(paymentUnderProcessing))
              }
            />
            <DetailDisplay
              title="Excess Amount Paid"
              value={
                INR_SYMBOL + Math.abs(reduceToFixedDecimals(excess_amount_paid))
              }
            />
          </div>
          <DisplayOrder
            selectedPackageSubscriptions={selectedOrderDetails.packages}
          />
          <div
            className={styles.addNote}
            onClick={() => handleOnClickAddNote(setOpenDialogContent)}
          >
            Add a Note?
            <AddCircleIcon />
          </div>
          {selectedOrderDetails && (
            <PaymentDetails
              paymentDetails={selectedOrderDetails.payment_details}
            />
          )}
        </div>
      </div>
      {paymentMethod === FULL_PAYMENT && (
        <div className={styles.toggle}>
          <Toggle
            checked={isOnlinePayment}
            onChange={(event) =>
              handleTogglePaymentMethod(event, setIsOnlinePayment)
            }
            label={
              isOnlinePayment
                ? SWITCH_TO_LOAN_PAYMENT
                : SWITCH_TO_ONLINE_PAYMENT
            }
            disabled={isLoanCreated}
          />
        </div>
      )}
      <Dialog open={openDialogContent !== null} height="180px">
        <AddNote
          onClose={handleDialogClose(setOpenDialogContent)}
          orderId={orderId}
        />
      </Dialog>
    </div>
  );
};

export default PaymentPreference;
